function normalizeHex(hex) {
  if (!hex || typeof hex !== "string") return null;
  const normalized = hex.replace("#", "").trim();
  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    return normalized.split("").map((char) => `${char}${char}`).join("");
  }
  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return normalized;
  }
  return null;
}

function hexToRgb(hex) {
  const normalized = normalizeHex(hex);
  if (!normalized) return null;
  return {
    r: parseInt(normalized.slice(0, 2), 16),
    g: parseInt(normalized.slice(2, 4), 16),
    b: parseInt(normalized.slice(4, 6), 16),
  };
}

function srgbToLinear(value) {
  const channel = value / 255;
  return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
}

function getRelativeLuminance(hex) {
  const rgb = hexToRgb(hex);
  if (!rgb) return null;
  const r = srgbToLinear(rgb.r);
  const g = srgbToLinear(rgb.g);
  const b = srgbToLinear(rgb.b);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

function getContrastRatio(bgHex, fgHex) {
  const bgLum = getRelativeLuminance(bgHex);
  const fgLum = getRelativeLuminance(fgHex);
  if (bgLum === null || fgLum === null) return 0;
  const lighter = Math.max(bgLum, fgLum);
  const darker = Math.min(bgLum, fgLum);
  return (lighter + 0.05) / (darker + 0.05);
}

function getReadableForegroundHex(backgroundHex, lightHex = "#FFFFFF", darkHex = "#1F2937") {
  const lightContrast = getContrastRatio(backgroundHex, lightHex);
  const darkContrast = getContrastRatio(backgroundHex, darkHex);
  return darkContrast >= lightContrast ? darkHex : lightHex;
}

export function hexToHslTriplet(hex, fallback) {
  const rgb = hexToRgb(hex);
  if (!rgb) return fallback;

  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;
  const d = max - min;

  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r:
        h = 60 * (((g - b) / d) % 6);
        break;
      case g:
        h = 60 * ((b - r) / d + 2);
        break;
      default:
        h = 60 * ((r - g) / d + 4);
        break;
    }
  }

  if (h < 0) h += 360;

  return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export function applyTenantTheme(tenant = {}) {
  const root = document.documentElement;
  const primaryHex = tenant.primary_color || "#8A9A86";
  const secondaryHex = tenant.secondary_color || "#D5CABD";
  const primaryForegroundHex = tenant.primary_foreground_color || getReadableForegroundHex(primaryHex, "#FFFFFF", "#111827");
  const secondaryForegroundHex = getReadableForegroundHex(secondaryHex, "#FFFFFF", "#111827");

  root.style.setProperty("--primary", hexToHslTriplet(primaryHex, "135 8% 56%"));
  root.style.setProperty("--primary-foreground", hexToHslTriplet(primaryForegroundHex, "0 0% 100%"));
  root.style.setProperty("--secondary", hexToHslTriplet(secondaryHex, "28 18% 79%"));
  root.style.setProperty("--secondary-foreground", hexToHslTriplet(secondaryForegroundHex, "220 13% 18%"));
  root.style.setProperty("--ring", hexToHslTriplet(primaryHex, "135 8% 56%"));
  root.style.setProperty("--chart-1", hexToHslTriplet(primaryHex, "135 8% 56%"));
  root.style.setProperty("--chart-2", hexToHslTriplet(secondaryHex, "28 18% 79%"));
  root.style.setProperty("--tenant-primary-foreground-hex", primaryForegroundHex);
  root.style.setProperty("--tenant-primary-hex", primaryHex);
  root.style.setProperty("--tenant-secondary-hex", secondaryHex);
}
